/**
 * Derive due/overdue display from persisted invoice fields.
 * Does not invent a new invoice status — overdue is a derived view only.
 */

export function resolveInvoiceDueOverview(invoice, now = new Date()) {
  if (!invoice) {
    return {
      kind: 'missing',
      dueAt: null,
      daysRemaining: null,
      overdueDays: null,
      isOverdue: false,
    };
  }

  const status = String(invoice.status || '').toLowerCase();
  const dueAtRaw = invoice.due_at || null;
  const dueAt = dueAtRaw ? new Date(dueAtRaw) : null;

  if (['paid', 'void', 'canceled'].includes(status)) {
    return {
      kind: status,
      dueAt,
      daysRemaining: null,
      overdueDays: null,
      isOverdue: false,
    };
  }

  if (!dueAt || Number.isNaN(dueAt.getTime())) {
    return {
      kind: 'missing_due',
      dueAt: null,
      daysRemaining: null,
      overdueDays: null,
      isOverdue: false,
    };
  }

  if (typeof invoice.overdue_days === 'number' && invoice.overdue_days > 0) {
    return {
      kind: 'overdue',
      dueAt,
      daysRemaining: null,
      overdueDays: invoice.overdue_days,
      isOverdue: true,
    };
  }

  if (typeof invoice.due_days_remaining === 'number') {
    return {
      kind: invoice.due_days_remaining === 0 ? 'due_today' : 'remaining',
      dueAt,
      daysRemaining: invoice.due_days_remaining,
      overdueDays: null,
      isOverdue: false,
    };
  }

  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const dueDay = new Date(dueAt);
  dueDay.setHours(0, 0, 0, 0);
  const diffMs = dueDay.getTime() - start.getTime();
  const days = Math.round(diffMs / 86400000);

  if (['open', 'pending'].includes(status) && days < 0) {
    return {
      kind: 'overdue',
      dueAt,
      daysRemaining: null,
      overdueDays: Math.abs(days),
      isOverdue: true,
    };
  }

  return {
    kind: days === 0 ? 'due_today' : 'remaining',
    dueAt,
    daysRemaining: Math.max(0, days),
    overdueDays: null,
    isOverdue: false,
  };
}

export function formatInvoiceDueOverview(overview, t, emptyValue = '—') {
  if (!overview) return emptyValue;
  if (overview.kind === 'paid') return t('billing:invoices.due.paid', { defaultValue: 'Paid' });
  if (overview.kind === 'void') return t('billing:invoices.due.void', { defaultValue: 'Void' });
  if (overview.kind === 'canceled') return t('billing:invoices.due.canceled', { defaultValue: 'Canceled' });
  if (overview.kind === 'missing_due' || overview.kind === 'missing') {
    return t('billing:invoices.due.missing', { defaultValue: 'No due date' });
  }
  if (overview.kind === 'overdue') {
    return t('billing:invoices.due.overdueDays', {
      count: overview.overdueDays,
      defaultValue: `Overdue by ${overview.overdueDays} day(s)`,
    });
  }
  if (overview.kind === 'due_today') {
    return t('billing:invoices.due.dueToday', { defaultValue: 'Due today' });
  }
  return t('billing:invoices.due.daysRemaining', {
    count: overview.daysRemaining,
    defaultValue: `${overview.daysRemaining} day(s) remaining`,
  });
}

export function defaultDueDateIso(days = 14, from = new Date()) {
  const d = new Date(from);
  d.setDate(d.getDate() + Number(days || 14));
  return d.toISOString().slice(0, 10);
}
